import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ErrorPage } from '@/components/common/ErrorPage';
import { Label, LABEL_TYPE, ToggleSwitch } from '@/components/generic';
import {
  BUTTON_TYPE,
  ButtonConfig,
  ButtonVariant
} from '@/components/generic/Button/button-config';
import { IconButtonVariant } from '@/components/generic/IconButton/icon-button-config';
import { DetailsPageLayout } from '@/components/layouts';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { queryClient } from '@/configuration/react-query';
import { AuthContext } from '@/contexts';
import { PERMISSION_TYPE, PRIVATE_PAGE_ROUTES } from '@/enums';
import { showToast, ToastType } from '@/helpers/toast';
import { EditPenIcon, TrashIcon, UsersIcon } from '@/resources';
import {
  useDeleteUserMutation,
  useGetUserByIdQuery,
  useGetUserInvitationLinkQuery,
  useResetMfaMutation
} from '@/services';
import { Device, Event } from '@/types';

export const UserDetails = () => {
  const [t] = useTranslation('common');
  const { userId } = useParams();
  const navigate = useNavigate();
  const { currentUserData: currentUser } = useContext(AuthContext);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState<boolean>(false);
  const [error, setError] = useState<number>();

  if (currentUser && Number(userId) === currentUser.id) {
    navigate(PRIVATE_PAGE_ROUTES.USERS);
  }

  const { data: user, isLoading: isUserLoading } = useGetUserByIdQuery(Number(userId), {
    onSuccess: () => {
      undefined;
    },
    onError: (error) => {
      setError(error.status);
    }
  });

  const { mutate: deleteUser } = useDeleteUserMutation(queryClient, {
    onSuccess: () => {
      navigate('/users');
      setIsOpenDeleteModal(false);
    },
    onError: () => {
      setIsOpenDeleteModal(false);
    }
  });

  const { mutate: resetMfa } = useResetMfaMutation(queryClient, {
    onSuccess: () => {
      showToast(ToastType.SUCCESS, t('mfaResetSuccess'));
    },
    onError: () => {
      showToast(ToastType.ERROR, t('mfaResetError'));
    }
  });

  const { data: invitationLink } = useGetUserInvitationLinkQuery(Number(userId), undefined, !!user);

  const handleEditUser = () => {
    navigate(`/users/${userId}/edit`);
  };

  const handleDelete = () => {
    setIsOpenDeleteModal(true);
  };

  const onCloseDeleteModal = () => {
    setIsOpenDeleteModal(false);
  };

  const handleCopyInvitationLink = () => {
    if (!invitationLink || !invitationLink.data) {
      showToast(ToastType.ERROR, t('copyInvitationLinkError'));
      return;
    }

    navigator.clipboard
      .writeText(invitationLink.data)
      .then(() => showToast(ToastType.SUCCESS, t('copyInvitationLinkSuccess')))
      .catch(() => showToast(ToastType.ERROR, t('copyInvitationLinkError')));
  };

  const headerButtonsConfig: ButtonConfig[] = [
    {
      icon: <EditPenIcon className='text-primary-blue' />,
      variant: IconButtonVariant.PRIMARY,
      type: BUTTON_TYPE.ICON_BUTTON,
      handleOnClick: handleEditUser,
      permissions: [PERMISSION_TYPE.UPDATE_USER]
    },
    {
      icon: <TrashIcon className='h-4 w-3 md:h-5 md:w-4' />,
      variant: IconButtonVariant.PRIMARY,
      type: BUTTON_TYPE.ICON_BUTTON,
      handleOnClick: handleDelete,
      permissions: [PERMISSION_TYPE.DELETE_USER]
    },
    {
      label: t('copyInvitationLink'),
      variant: ButtonVariant.PRIMARY,
      type: BUTTON_TYPE.BUTTON,
      handleOnClick: handleCopyInvitationLink,
      isHidden: !!user?.data?.registeredAt
    },
    {
      label: t('resetMfa'),
      variant: ButtonVariant.PRIMARY,
      type: BUTTON_TYPE.BUTTON,
      handleOnClick: () => resetMfa(Number(userId)),
      permissions: [PERMISSION_TYPE.RESET_MFA_FOR_USER],
      isHidden: !user?.data?.isMfaEnabled
    }
  ];

  return error ? (
    <ErrorPage code={error} />
  ) : (
    <div>
      <DetailsPageLayout
        icon={<UsersIcon className='size-[17px]' />}
        iconPath={'/users'}
        path={[{ label: t('userDetails') }]}
        title={`${user?.data.firstName} ${user?.data.lastName}`}
        tag={
          user?.data?.registeredAt === undefined
            ? 'Unregistered'
            : user?.data?.enabled
              ? 'Active'
              : 'Inactive'
        }
        isLoading={isUserLoading}
        headerButtons={headerButtonsConfig}>
        {
          <>
            <div className='mt-3 flex md:mt-12'>
              <div className='flex w-full flex-col gap-4'>
                <div className='flex w-full flex-col md:flex-row md:items-center'>
                  <Label
                    text={`${t('email')}:`}
                    type={LABEL_TYPE.PARAGRAPH1}
                    className='w-1/6 pb-2.5 text-secondary-gray sm:p-0'
                  />
                  <Label
                    text={user?.data?.email}
                    type={LABEL_TYPE.PARAGRAPH1}
                    className='!text-primary-black'
                  />
                </div>
                <div className='flex w-full flex-col md:flex-row md:items-center'>
                  <Label
                    text={`${t('role')}:`}
                    type={LABEL_TYPE.PARAGRAPH1}
                    className='w-1/6 pb-2.5 text-secondary-gray sm:p-0'
                  />
                  <Label
                    text={user?.data?.role?.name}
                    type={LABEL_TYPE.PARAGRAPH1}
                    className='!text-primary-black'
                  />
                </div>
                <div className='flex w-full flex-col md:flex-row md:items-center'>
                  <Label
                    text={`${t('mfaTitle')}:`}
                    type={LABEL_TYPE.PARAGRAPH1}
                    className='w-1/6 pb-2.5 text-secondary-gray sm:p-0'
                  />
                  <div className='flex flex-row items-center gap-x-2'>
                    <ToggleSwitch toggled={user?.data?.isMfaEnabled} />
                    <Label
                      text={user?.data?.isMfaEnabled ? t('mfaEnabled') : t('mfaDisabled')}
                      type={LABEL_TYPE.PARAGRAPH1}
                      className={'!text-primary-black'}
                    />
                  </div>
                </div>
                {!user?.data?.role?.permissions?.includes(PERMISSION_TYPE.GLOBAL_ACCESS) && (
                  <div className='flex w-full flex-col md:flex-row md:items-center'>
                    <Label
                      text={`${t('organization')}:`}
                      type={LABEL_TYPE.PARAGRAPH1}
                      className='w-1/6 pb-2.5 text-secondary-gray sm:p-0'
                    />
                    <Label
                      text={user?.data?.organizations
                        ?.map((org: { name: string }) => org.name)
                        .join(', ')}
                      type={LABEL_TYPE.PARAGRAPH1}
                      className='!text-primary-black'
                    />
                  </div>
                )}
                {user?.data?.devices?.length > 0 && (
                  <div className='mt-5 flex w-full flex-col border-t border-solid border-secondary-pale-blue-gray pt-5'>
                    <Label text={t('devices')} type={LABEL_TYPE.H3} className='w-1/6 pt-2.5' />
                    {user?.data.devices.map((device: Device, i: number) => (
                      <Label
                        key={i}
                        text={`${i + 1}. ${device.name}`}
                        type={LABEL_TYPE.PARAGRAPH1}
                        className='pt-2.5 !text-primary-black'
                      />
                    ))}
                  </div>
                )}
                {user?.data?.events?.length > 0 && (
                  <div className='mt-5 flex w-full flex-col border-t border-solid border-secondary-pale-blue-gray pt-5'>
                    <Label text={t('events')} type={LABEL_TYPE.H3} className='w-1/6 pt-2.5' />
                    {user?.data.events.map((event: Event, i: number) => (
                      <Label
                        key={i}
                        text={`${i + 1}. ${event.name}`}
                        type={LABEL_TYPE.PARAGRAPH1}
                        className='pt-2.5 !text-primary-black'
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        }
      </DetailsPageLayout>
      {isOpenDeleteModal && (
        <ConfirmationModal
          onConfirm={() => deleteUser(user?.data?.id)}
          isOpen={isOpenDeleteModal}
          onClose={onCloseDeleteModal}
          message={t('deleteMessage', {
            item: `${user?.data.firstName} ${user?.data.lastName}`
          })}
        />
      )}
    </div>
  );
};
